import { types as sdkTypes } from './sdkLoader';

const { UUID, Money, LatLng } = sdkTypes;

const META_PREFIX = 'meta_';
const PUB_PREFIX = 'pub_';
const DEFAULT_PER_PAGE = 24;
const DEFAULT_INDEX_LISTING_STATE = 'published';
const DEFAULT_CURRENCY = 'USD';

const SORT_ASC = 'asc';
const SORT_DESC = 'desc';

// Mirror of the index's configured filterable/sortable attributes (infrastructure
// meili-sync settings; see docs meilisearch.md). Meili 400s the whole request when
// a filter or sort names an attribute outside these, so unknown clauses are dropped
// here rather than forwarded. Keep these in lockstep with the index settings.
const FILTERABLE_ATTRIBUTES = new Set([
  'categoryLevel1', 'categoryLevel2', 'categoryLevel3', 'condition', 'price', '_geo',
  'sellerId', 'isBoosted', 'isReduced', 'isSpotlight', 'state', 'objectID',
  'listingType', 'transactionProcessAlias', 'unitType',
]);
const SORTABLE_ATTRIBUTES = new Set([
  'price', 'createdAt', 'reviewAvg', 'isBoosted', 'sellerTier', 'isReduced', '_geo',
]);

const MILES_TO_METERS = 1609.344;

const parseLatLngPair = (value: unknown): { lat: number; lng: number } | null => {
  if (typeof value !== 'string') return null;
  const [latRaw, lngRaw] = value.split(',', 2);
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const translateRadius = (origin: unknown, distance: unknown): string | null => {
  const point = parseLatLngPair(origin);
  if (!point) return null;
  const miles = Number(distance);
  if (!Number.isFinite(miles) || miles <= 0) return null;
  return `_geoRadius(${point.lat}, ${point.lng}, ${Math.round(miles * MILES_TO_METERS)})`;
};


const ENTITY_TYPE = {
  Listing: 'listing',
  User: 'user',
  Image: 'image',
} as const;

const IMAGE_VARIANTS = {
  'listing-card': { width: 400, height: 400 },
  'listing-card-2x': { width: 800, height: 800 },
  'scaled-medium': { width: 750, height: 750 },
  'scaled-small': { width: 320, height: 320 },
} as const;

export type SharetribeQueryParams = Record<string, unknown>;

export type MeiliSearchBody = {
  q?: string;
  filter?: string;
  sort?: string[];
  hitsPerPage: number;
  page: number;
};

export type MeiliImage = { id: string; url: string };

export type MeiliListingHit = {
  objectID: string;
  title?: string;
  description?: string;
  state?: string;
  createdAt?: number;
  price?: number;
  currency?: string;
  categoryLevel1?: string;
  categoryLevel2?: string;
  categoryLevel3?: string;
  condition?: string;
  addressCity?: string;
  addressState?: string;
  addressZipcode?: string;
  oldPrice?: number;
  isBoosted?: number;
  isReduced?: number;
  isSpotlight?: number;
  reviewAvg?: number | null;
  reviewCount?: number;
  sellerId?: string;
  sellerName?: string;
  sellerTier?: number;
  sellerVerified?: boolean;
  _geo?: { lat: number; lng: number };
  images?: MeiliImage[];
};

export type MeiliSearchResponse = {
  hits?: MeiliListingHit[];
  estimatedTotalHits?: number;
  totalHits?: number;
  hitsPerPage?: number;
  page?: number;
  totalPages?: number;
};

const isAscPrefixed = (key: string) => key.startsWith('-');
const stripDirection = (key: string) => (isAscPrefixed(key) ? key.slice(1) : key);

/**
 * Translate one Sharetribe sort token to a Meili sort entry.
 * Sharetribe convention: bare `meta_*` = desc, bare non-meta = asc.
 *
 * @param token e.g. `-createdAt`, `meta_isBoosted`
 * @returns Meili `<field>:<asc|desc>`
 */
export const translateSortToken = (token: string): string => {
  const noDir = stripDirection(token);
  const isMeta = noDir.startsWith(META_PREFIX);
  const field = isMeta ? noDir.slice(META_PREFIX.length) : noDir;
  const dir = isMeta
    ? isAscPrefixed(token) ? SORT_ASC : SORT_DESC
    : isAscPrefixed(token) ? SORT_DESC : SORT_ASC;
  return `${field}:${dir}`;
};

const translateSort = (sortString: unknown): string[] | undefined => {
  if (typeof sortString !== 'string' || sortString.length === 0) return undefined;
  const tokens = sortString
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .filter(token => {
      const noDir = stripDirection(token);
      const field = noDir.startsWith(META_PREFIX) ? noDir.slice(META_PREFIX.length) : noDir;
      return SORTABLE_ATTRIBUTES.has(field);
    })
    .map(translateSortToken);
  return tokens.length ? tokens : undefined;
};

const escapeFilterString = (s: unknown): string =>
  `"${String(s).replace(/"/g, '\\"')}"`;

const translateRange = (field: string, value: string): string[] => {
  if (!value.includes(',')) return [];
  const [minRaw, maxRaw] = value.split(',', 2);
  const out: string[] = [];
  if (minRaw !== '') {
    const min = Number(minRaw);
    if (Number.isFinite(min)) out.push(`${field} >= ${min}`);
  }
  if (maxRaw !== '') {
    const max = Number(maxRaw);
    if (Number.isFinite(max)) out.push(`${field} <= ${max}`);
  }
  return out;
};

const translateBounds = (boundsString: string): string | null => {
  const parts = boundsString.split(',').map(Number);
  if (parts.length !== 4 || parts.some(Number.isNaN)) return null;
  const [neLat, neLng, swLat, swLng] = parts;
  return `_geoBoundingBox([${neLat}, ${neLng}], [${swLat}, ${swLng}])`;
};

const translateStateFilter = (value: unknown): string | null => {
  const states = String(value).split(',').filter(Boolean);
  if (states.length === 0) return null;
  if (states.length === 1) return `state = ${escapeFilterString(states[0])}`;
  return `(${states.map(s => `state = ${escapeFilterString(s)}`).join(' OR ')})`;
};

const translatePubFilter = (rawKey: string, rawValue: unknown): string | null => {
  const field = rawKey.slice(PUB_PREFIX.length);
  if (!FILTERABLE_ATTRIBUTES.has(field)) return null;
  const valueStr = String(rawValue);

  if (valueStr.includes(',')) {
    const head = valueStr.split(',')[0];
    const looksNumeric = /^-?\d+(\.\d+)?$/.test(head);
    if (looksNumeric) {
      const range = translateRange(field, valueStr);
      if (range.length) return range.join(' AND ');
    }
    const vals = valueStr.split(',').filter(Boolean);
    return `(${vals.map(v => `${field} = ${escapeFilterString(v)}`).join(' OR ')})`;
  }

  return `${field} = ${escapeFilterString(valueStr)}`;
};

const translateMetaFilter = (rawKey: string, rawValue: unknown): string | null => {
  const field = rawKey.slice(META_PREFIX.length);
  if (!FILTERABLE_ATTRIBUTES.has(field)) return null;
  const valueStr = String(rawValue);

  if (valueStr.includes(',')) {
    const range = translateRange(field, valueStr);
    return range.length ? range.join(' AND ') : null;
  }

  const num = Number(valueStr);
  return Number.isFinite(num)
    ? `${field} = ${num}`
    : `${field} = ${escapeFilterString(valueStr)}`;
};

/**
 * Combine Sharetribe filter params (`pub_*`, `meta_*`, `price`, `bounds`,
 * `state`) into one Meili filter expression joined with `AND`.
 *
 * @returns single filter string, or undefined if no clauses apply
 */
export const translateFilters = (params: SharetribeQueryParams): string | undefined => {
  const clauses: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue;

    if (key === 'state') {
      const clause = translateStateFilter(value);
      if (clause) clauses.push(clause);
      continue;
    }

    if (key === 'price') {
      clauses.push(...translateRange('price', String(value)));
      continue;
    }

    if (key === 'bounds') {
      const bbox = translateBounds(String(value));
      if (bbox) clauses.push(bbox);
      continue;
    }

    if (key === 'author_id') {
      const uuid = (value as { uuid?: string })?.uuid ?? value;
      clauses.push(`sellerId = ${escapeFilterString(uuid)}`);
      continue;
    }

    if (key.startsWith(PUB_PREFIX)) {
      const clause = translatePubFilter(key, value);
      if (clause) clauses.push(clause);
      continue;
    }

    if (key.startsWith(META_PREFIX)) {
      const clause = translateMetaFilter(key, value);
      if (clause) clauses.push(clause);
    }
  }

  const hasBounds = clauses.some(c => c.startsWith('_geoBoundingBox'));
  if (!hasBounds) {
    const radius = translateRadius(params.origin, params.distance);
    if (radius) clauses.push(radius);
  }

  return clauses.length ? clauses.join(' AND ') : undefined;
};


const buildGeoSort = (lat: number, lng: number): string => `_geoPoint(${lat}, ${lng}):asc`;

/**
 * Translate Sharetribe SDK `listings.query` params into a Meili search body.
 *
 * @param params Sharetribe SDK query params
 * @returns Meili POST body for `/indexes/{uid}/search`
 */
export const paramsToMeili = (params: SharetribeQueryParams = {}): MeiliSearchBody => {
  const body: MeiliSearchBody = {
    hitsPerPage: Number(params.perPage) || DEFAULT_PER_PAGE,
    page: Number(params.page) || 1,
  };

  if (params.keywords) body.q = String(params.keywords);

  const filter = translateFilters(params);
  if (filter) body.filter = filter;

  const userSort = translateSort(params.sort) || [];
  const originPoint = parseLatLngPair(params.origin);
  const sort = originPoint ? [buildGeoSort(originPoint.lat, originPoint.lng), ...userSort] : userSort;
  if (sort.length) body.sort = sort;

  return body;
};

const synthesiseImageEntity = (img: MeiliImage) => ({
  id: new UUID(img.id),
  type: ENTITY_TYPE.Image,
  attributes: {
    variants: Object.fromEntries(
      Object.entries(IMAGE_VARIANTS).map(([name, dims]) => [name, { url: img.url, ...dims }])
    ),
  },
});

const synthesiseAuthorEntity = (hit: MeiliListingHit) => ({
  id: new UUID(hit.sellerId as string),
  type: ENTITY_TYPE.User,
  attributes: {
    profile: {
      displayName: hit.sellerName || '',
      abbreviatedName: (hit.sellerName || '').slice(0, 2).toUpperCase(),
      metadata: {
        membershipTier: hit.sellerTier ?? 0,
        isIdentityVerified: hit.sellerVerified ? 1 : 0,
      },
    },
  },
});

const synthesiseListingEntity = (hit: MeiliListingHit) => ({
  id: new UUID(hit.objectID),
  type: ENTITY_TYPE.Listing,
  attributes: {
    title: hit.title || '',
    description: hit.description || '',
    state: hit.state || DEFAULT_INDEX_LISTING_STATE,
    createdAt: hit.createdAt ? new Date(hit.createdAt * 1000) : null,
    publicData: {
      categoryLevel1: hit.categoryLevel1,
      categoryLevel2: hit.categoryLevel2,
      categoryLevel3: hit.categoryLevel3,
      condition: hit.condition,
      oldPrice: hit.oldPrice,
      location: {
        city: hit.addressCity,
        state: hit.addressState,
        zipcode: hit.addressZipcode,
      },
    },
    metadata: {
      isBoosted: hit.isBoosted ?? 0,
      isReduced: hit.isReduced ?? 0,
      isSpotlight: hit.isSpotlight ?? 0,
      reviewAvg: hit.reviewAvg ?? null,
      reviewCount: hit.reviewCount ?? 0,
    },
    price: hit.price != null ? new Money(hit.price, hit.currency || DEFAULT_CURRENCY) : null,
    geolocation: hit._geo ? new LatLng(hit._geo.lat, hit._geo.lng) : null,
  },
  relationships: {
    author: hit.sellerId
      ? { data: { id: new UUID(hit.sellerId), type: ENTITY_TYPE.User } }
      : { data: null },
    images: {
      data: (hit.images || []).map(img => ({ id: new UUID(img.id), type: ENTITY_TYPE.Image })),
    },
  },
});

/**
 * Translate a Meili search response into the shape `sdk.listings.query()`
 * returns. Synthesises `included` author + image entities from denormalised
 * fields so `addMarketplaceEntities` resolves relationships normally.
 *
 * @param meiliResp raw Meili search response
 * @returns SDK-shaped `{ data: { data, included, meta } }`
 */
export const hitsToSharetribe = (meiliResp: MeiliSearchResponse = {}) => {
  const hits = meiliResp.hits || [];
  const data = hits.map(synthesiseListingEntity);

  const includedById = new Map<string, ReturnType<typeof synthesiseAuthorEntity | typeof synthesiseImageEntity>>();
  for (const hit of hits) {
    if (hit.sellerId && !includedById.has(`${ENTITY_TYPE.User}:${hit.sellerId}`)) {
      includedById.set(`${ENTITY_TYPE.User}:${hit.sellerId}`, synthesiseAuthorEntity(hit));
    }
    for (const img of hit.images || []) {
      if (img?.id && !includedById.has(`${ENTITY_TYPE.Image}:${img.id}`)) {
        includedById.set(`${ENTITY_TYPE.Image}:${img.id}`, synthesiseImageEntity(img));
      }
    }
  }

  const totalItems =
    meiliResp.estimatedTotalHits ?? meiliResp.totalHits ?? hits.length;
  const perPage = meiliResp.hitsPerPage ?? DEFAULT_PER_PAGE;
  const page = meiliResp.page ?? 1;
  const totalPages =
    meiliResp.totalPages ?? (perPage > 0 ? Math.ceil(totalItems / perPage) : 1);

  return {
    data: {
      data,
      included: Array.from(includedById.values()),
      meta: { totalItems, totalPages, page, perPage },
    },
  };
};
